import { ChevronDown, ChevronRight, Radar } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cx, Spinner } from "../../components/ui";
import { api } from "../../lib/api";

interface Req {
  id: string;
  name: string;
  cls: string;
  action: string;
  status: string;
  state: "complete" | "failed" | "pending" | "unknown";
  error?: string;
}
interface Group extends Req {
  groupStatus: string;
  created: string;
  job?: string;
  label?: string;
  requests: Req[];
}
interface Activity {
  at: string;
  flags?: { schedule: string; org: string; values: Record<string, string>; locked?: boolean };
  groups: Group[];
  truncated: boolean;
  ended: boolean;
}

const POLL_MS = 20_000;
/** Nothing has changed in Infor or the run for this long: stop reading until the run moves again. */
const QUIET_MS = 15 * 60_000;
const PREF = "ja.inforActivity";
const tone = (s: Req["state"]) => (s === "complete" ? "text-good" : s === "failed" ? "text-bad" : s === "pending" ? "text-warn" : "text-muted");
/** Infor's 16-digit CreatedStamp (YYYYMMDDHHMMSS..) as local time. */
const stampTime = (s: string) => {
  const d = s.replace(/\D/g, "");
  if (d.length < 14) return s;
  return new Date(Date.UTC(+d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6, 8), +d.slice(8, 10), +d.slice(10, 12), +d.slice(12, 14))).toLocaleTimeString();
};
const prefGet = () => {
  try {
    return window.localStorage.getItem(PREF) === "on";
  } catch {
    return false;
  }
};
const prefSet = (on: boolean) => {
  try {
    window.localStorage.setItem(PREF, on ? "on" : "off");
  } catch {
    /* private window: the choice just isn't remembered */
  }
};

/** What has to differ for the panel to count as changed: flags, and each group's and request's status. */
const signature = (a: Activity) => JSON.stringify([a.flags?.values, a.groups.map((g) => [g.id, g.status, g.groupStatus, g.requests.map((r) => r.status)])]);

/**
 * The Infor activity panel (the Waypoint monitor, Watch-Payroll, inside the portal; Chad 2026-10-10): the payment
 * schedule's run flags and every request group Infor started during the run, read live through the run's connection
 * every 20 s. Shown when the organization has it switched on; each person turns it on or off. It only reads while it's
 * needed (Chad, 2026-10-10): not after the run ends, not once nothing has changed for 15 minutes (it picks up again when
 * the run moves, `moved` being the run's last save), and not while the browser is idle.
 */
export function InforActivity({ runId, live, moved, idle }: { runId: string; live: boolean; moved: string; idle: boolean }) {
  const [on, setOn] = useState(prefGet);
  const [data, setData] = useState<Activity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const prevFlags = useRef<Record<string, string>>({});
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const lastSig = useRef("");
  const lastChange = useRef(Date.now());
  const [quiet, setQuiet] = useState(false);

  const load = useCallback(async () => {
    try {
      const a = await api<Activity>(`/apps/ja/runs/${runId}/activity`);
      const moved = new Set(Object.entries(a.flags?.values ?? {}).filter(([k, v]) => prevFlags.current[k] !== undefined && prevFlags.current[k] !== v).map(([k]) => k));
      prevFlags.current = a.flags?.values ?? {};
      setChanged(moved);
      const sig = signature(a);
      if (sig !== lastSig.current) {
        lastSig.current = sig;
        lastChange.current = Date.now();
      }
      setData(a);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [runId]);

  // The first read when switched on (an ended run: the final picture, then no more).
  useEffect(() => {
    if (on) void load();
  }, [on, load]);
  // The run moved (a step, a report, a person): that's activity, so a quiet panel wakes up and reads now.
  useEffect(() => {
    lastChange.current = Date.now();
    if (on && quiet && live) void load();
    setQuiet(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moved]);
  const ended = !live || data?.ended === true;
  useEffect(() => {
    if (!on || ended || idle || quiet) return;
    const t = setInterval(() => {
      if (Date.now() - lastChange.current > QUIET_MS) setQuiet(true);
      else void load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [on, ended, idle, quiet, load]);
  // Back from idle: catch up straight away.
  const wasIdle = useRef(idle);
  useEffect(() => {
    if (wasIdle.current && !idle && on && !ended && !quiet) void load();
    wasIdle.current = idle;
  }, [idle, on, ended, quiet, load]);
  const mode = !on ? "Off" : ended ? "Run ended: final picture, not reading Infor" : idle ? "Paused while this page isn't in use" : quiet ? "Paused: nothing has changed for 15 minutes; picks up when the run moves" : "On: reads Infor every 20 seconds";

  const toggleGroup = (id: string) =>
    setOpenGroups((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="font-display font-semibold text-ink flex items-center gap-2">
          <Radar className="size-4" aria-hidden /> Infor activity
        </h2>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            role="switch"
            checked={on}
            onChange={(e) => {
              setOn(e.target.checked);
              prefSet(e.target.checked);
            }}
          />
          {mode}
        </label>
      </div>
      {!on ? (
        <p className="text-sm text-muted">Turn on to watch what Infor is doing for this run: the payment schedule's run flags and every request group it started, read live.</p>
      ) : !data && !error ? (
        <Spinner />
      ) : (
        <div className="rounded-xl border border-line bg-surface p-4 space-y-3 text-sm">
          {error && <p className="text-bad text-xs">{error}</p>}
          {data?.flags && (
            <div>
              <div className="text-xs text-muted mb-1">
                Payment schedule {data.flags.schedule} (HR organization {data.flags.org}){data.flags.locked !== undefined && <> · payroll {data.flags.locked ? "locked" : "not locked"}</>}
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.flags.values).map(([k, v]) => (
                  <span key={k} className={cx("rounded-full border px-2 py-0.5 text-xs", changed.has(k) ? "border-accent bg-accent-soft text-accent-text" : "border-line text-ink-2")} title={changed.has(k) ? "Changed since the last read" : undefined}>
                    {k} <span className="font-mono">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {data && (
            <div>
              <div className="text-xs text-muted mb-1">
                {data.groups.length} request group{data.groups.length === 1 ? "" : "s"} since the run started, newest first · read {new Date(data.at).toLocaleTimeString()}
                {data.truncated && " · more than one read shows: the newest are listed"}
              </div>
              <ul className="divide-y divide-line rounded-lg border border-line max-h-96 overflow-y-auto">
                {data.groups.map((g) => {
                  const open = openGroups.has(g.id);
                  const pending = g.requests.filter((r) => r.state === "pending").length;
                  const failed = [g, ...g.requests].filter((r) => r.state === "failed").length;
                  return (
                    <li key={g.id} className="px-3 py-1.5">
                      <button type="button" className="w-full text-left flex items-start gap-2" onClick={() => toggleGroup(g.id)} aria-expanded={open} disabled={!g.requests.length}>
                        {g.requests.length ? open ? <ChevronDown className="size-4 mt-0.5 shrink-0" aria-hidden /> : <ChevronRight className="size-4 mt-0.5 shrink-0" aria-hidden /> : <span className="size-4 shrink-0" />}
                        <span className="text-xs text-muted w-20 shrink-0 tabular pt-0.5">{stampTime(g.created)}</span>
                        <span className="flex-1 min-w-0">
                          {g.job && <span className="mr-1 rounded bg-accent-soft text-accent-text px-1.5 text-xs font-medium">{g.job}</span>}
                          <span className="font-mono text-xs">
                            {g.cls}.{g.action}
                          </span>
                          {g.label && <span className="text-xs text-muted"> · {g.label}</span>}
                          <span className="block text-xs">
                            <span className={tone(g.state)}>{g.status || "—"}</span>
                            {g.requests.length > 0 && (
                              <span className="text-muted">
                                {" "}
                                · group {g.groupStatus || "—"}, {g.requests.length + 1} requests{pending ? `, ${pending} in process` : ""}
                                {failed ? <span className="text-bad">, {failed} failed</span> : ""}
                              </span>
                            )}
                          </span>
                          {g.error && <span className="block text-xs text-bad">{g.error}</span>}
                        </span>
                      </button>
                      {open && (
                        <ul className="ml-28 mt-1 space-y-0.5">
                          {g.requests.map((r) => (
                            <li key={r.id} className="text-xs">
                              <span className="font-mono">
                                {r.cls}.{r.action}
                              </span>{" "}
                              <span className={tone(r.state)}>{r.status}</span>
                              {r.error && <span className="block text-bad">{r.error}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
                {!data.groups.length && <li className="px-3 py-2 text-muted">Infor shows no requests since the run started.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
